import { StructuredDataSchemaItem } from '@repo/shared/dtos/structured-data'
import { useT } from '@repo/ui/hooks'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@repo/ui/hoverCard'
import { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'

import { DataTableColumnHeader } from '@/components/admin/members/ColHeader'
import { BeneficiaryLink } from '@/components/beneficiary/BeneficiaryLink'
import { DisplayValue } from '@/components/beneficiary/BeneficiaryStructuredData'
import {
  BeneficiaryListDataItem,
  BeneficiaryListTableProps,
} from '@/components/beneficiary/list/table/Table'

export const useBeneficiaryListTableColumns = ({
  organization,
  onSelectBeneficiary,
}: Pick<BeneficiaryListTableProps, 'onSelectBeneficiary' | 'organization'> & {
  data: BeneficiaryListDataItem[]
}) => {
  const dynamicColumns = useMemo(
    () =>
      createDynamicColumns(organization.structuredDataDisplay, organization.structuredDataSchema),
    [organization.structuredDataDisplay, organization.structuredDataSchema],
  )

  const staticColumns = createStaticColumns(onSelectBeneficiary)

  const allColumns = useMemo(
    () => [...staticColumns, ...dynamicColumns],
    [staticColumns, dynamicColumns],
  )

  return allColumns
}

const createStaticColumns = (
  onSelectBeneficiary?: () => void,
): ColumnDef<BeneficiaryListDataItem>[] => {
  const { tc } = useT()
  return useMemo(
    () => [
      {
        accessorKey: 'fullName',
        enableHiding: false,
        meta: { defaultHidden: false, label: 'fullName', type: 'string' },
        header: ({ column }) => (
          <DataTableColumnHeader
            title={tc('fullName', { cfl: true })}
            column={column}
            options={{ sortable: true }}
          />
        ),
        cell: ({ row }) => (
          <BeneficiaryLink
            onClick={onSelectBeneficiary}
            id={row.original.id}
            fullName={row.original.fullName}
          />
        ),
      },
      {
        accessorKey: 'profile',
        meta: { defaultHidden: false, label: 'profile', type: 'string' },
        header: ({ column }) => (
          <DataTableColumnHeader title={tc('profile', { cfl: true })} column={column} />
        ),
        cell: ({ column, row }) => {
          const value = row.getValue<string>(column.id)
          return (
            <HoverCard>
              <HoverCardTrigger asChild>
                <div className="line-clamp-1 max-w-sm select-none whitespace-pre-wrap break-words">
                  {value}
                </div>
              </HoverCardTrigger>
              <HoverCardContent className="w-96">{value}</HoverCardContent>
            </HoverCard>
          )
        },
      },
    ],
    [],
  )
}

const createDynamicColumns = (
  structuredDataDisplay: BeneficiaryListTableProps['organization']['structuredDataDisplay'],
  structuredDataSchema: BeneficiaryListTableProps['organization']['structuredDataSchema'],
): ColumnDef<BeneficiaryListDataItem>[] => {
  if (!structuredDataDisplay || !structuredDataSchema) {
    return []
  }

  const dynamicCols: ColumnDef<BeneficiaryListDataItem>[] = []

  structuredDataDisplay.forEach((section) => {
    section.structuredDataIds.forEach((itemId) => {
      const schemaItem = structuredDataSchema.find((s) => s.id === itemId)

      if (schemaItem) {
        dynamicCols.push({
          id: schemaItem.id,
          accessorKey: schemaItem.id,
          enableHiding: true,
          meta: { defaultHidden: true, label: schemaItem.label, type: schemaItem.type },
          header: ({ column }) => {
            return (
              <DataTableColumnHeader
                title={schemaItem.label}
                column={column}
                options={{ sortable: true }}
              />
            )
          },
          cell: ({ getValue }) => {
            const value = getValue() as StructuredDataSchemaItem['defaultValue']
            return <DisplayValue value={value} type={schemaItem.type} />
          },
        })
      }
    })
  })
  return dynamicCols
}
